import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useCreateThread, useEditThreadMetadata } from '@liveblocks/react/suspense'
import { CommentMetadata, QueuedResolutionStatus } from './types'
import { LiveblocksErrorBoundary } from './error-boundary'
import { SortBy } from './comments-select'
import { getCommentText, useFetchAllThreads } from './helpers'
import { EditList } from './edit-list'
import type { CommentBody, ThreadData } from '@liveblocks/client'
import { useBroadcastEvent, useEventListener } from '@liveblocks/react/suspense'
import { useReviewLock } from './hooks'

const LiveblocksEditListContent = () => {
  const [threads, setThreads] = useState<ThreadData[]>([])
  const [selectedCommentType, setSelectedCommentType] = useState<SortBy>(SortBy.ALL_TYPES)
  const [activeCommentTarget, setActiveCommentTarget] = useState<{
    id: string
    position: { top: number; left: number } | null
  } | null>(null)
  const [hasPendingComments, setHasPendingComments] = useState(false)

  const queuedResolutionsRef = useRef<
    Map<
      string,
      {
        thread: ThreadData
        status: QueuedResolutionStatus
      }
    >
  >(new Map())

  const initialThreads = useFetchAllThreads()
  const createThread = useCreateThread()
  const editThreadMetadata = useEditThreadMetadata()

  const reviewLock = useReviewLock()

  // Add broadcast and useEventListener hooks
  const broadcast = useBroadcastEvent()
  const onRefetchRef = useRef<(() => void) | null>(null)

  const handleRefetch = useCallback((callback: () => void) => {
    onRefetchRef.current = callback
  }, [])

  useEventListener(({ event }) => {
    if (event.type === 'REFETCH' && onRefetchRef.current) {
      onRefetchRef.current()
    }
  })

  const checkPendingStatus = useCallback(
    (threadsToCheck: ThreadData[]) =>
      threadsToCheck.some(thread => {
        const queuedResolution = queuedResolutionsRef.current.get(thread.id)

        // If there's a queued resolution, use its state
        if (queuedResolution) {
          return false // Thread is resolved if it has a queued resolution
        }

        // Else use the thread's current state
        return !thread.resolved && thread.metadata.status === 'pending'
      }),
    [],
  )

  // Sync with liveblocks threads
  useEffect(() => {
    setThreads(initialThreads)
    setHasPendingComments(checkPendingStatus(initialThreads))
  }, [initialThreads, checkPendingStatus])

  const updateLocalThreadStatus = useCallback(
    (threadId: string, status: QueuedResolutionStatus) => {
      setThreads(prevThreads => {
        const updatedThreads = prevThreads.map(thread => {
          if (thread.id !== threadId) {
            return thread
          }

          // Update both the thread state and metadata
          const updatedThread = {
            ...thread,
            resolved: true,
            metadata: {
              ...thread.metadata,
              status,
              moderatedAt: new Date().toISOString(),
            },
          }

          // Update the queued resolution to match
          queuedResolutionsRef.current.set(threadId, {
            thread: updatedThread,
            status,
          })

          return updatedThread
        })
        // Update hasPendingComments based on the new threads state
        setHasPendingComments(checkPendingStatus(updatedThreads))
        return updatedThreads
      })
    },
    [checkPendingStatus],
  )

  const queueThreadResolution = useCallback(
    (thread: ThreadData, status: QueuedResolutionStatus) => {
      queuedResolutionsRef.current.set(thread.id, { thread, status })
    },
    [],
  )

  const handlePendingThreadResolutions = useCallback(async () => {
    const resolutions = Array.from(queuedResolutionsRef.current.values())

    await Promise.all(
      resolutions.map(({ thread, status }) => {
        // For approved comments, update currentValue to the approved text
        const updatedMetadata = {
          ...thread.metadata,
          userId: thread.metadata.userId,
          moderatedById: thread.metadata.moderatedById,
          status,
          resolved: true,
          moderatedAt: new Date().toISOString(),
        }

        // If approved, set currentValue to the approved comment text
        if (status === 'approved') {
          const approvedText = getCommentText(thread)
          if (approvedText) {
            updatedMetadata.currentValue = approvedText
          }
        }

        editThreadMetadata({
          threadId: thread.id,
          metadata: updatedMetadata,
        })
      }),
    )

    queuedResolutionsRef.current.clear()
  }, [editThreadMetadata])

  // Wrap createThread to handle local state updates
  const handleCreateThread = useCallback(
    (params: { body: CommentBody; metadata: CommentMetadata }) => {
      const newThread = createThread(params)
      if (newThread.id) {
        // Check if thread already exists before adding
        setThreads(prev => {
          const threadExists = prev.some(thread => thread.id === newThread.id)
          if (threadExists) return prev

          // Get all threads that have queued resolutions
          const queuedThreadIds = new Set(Array.from(queuedResolutionsRef.current.keys()))

          // Preserve existing threads with their current state
          const existingThreads = prev.map(thread => {
            // If this thread has a queued resolution, use that state
            if (queuedThreadIds.has(thread.id)) {
              const queuedResolution = queuedResolutionsRef.current.get(thread.id)
              if (queuedResolution?.status) {
                return {
                  ...thread,
                  resolved: true,
                  metadata: {
                    ...thread.metadata,
                    status: queuedResolution.status,
                    moderatedAt: thread.metadata.moderatedAt,
                  },
                }
              }
            }
            // For threads without queued resolutions, keep their current state
            return thread
          })

          // Add the new thread
          return [...existingThreads, newThread]
        })
        setHasPendingComments(true)
      }
      return newThread
    },
    [createThread],
  )

  const sortThreads = useCallback(
    (threadsToSort: ThreadData[]) =>
      [...threadsToSort].sort((a, b) => {
        const queuedResolutionA = queuedResolutionsRef.current.get(a.id)
        const queuedResolutionB = queuedResolutionsRef.current.get(b.id)

        const isPendingA = !queuedResolutionA && !a.resolved && a.metadata.status === 'pending'
        const isPendingB = !queuedResolutionB && !b.resolved && b.metadata.status === 'pending'

        if (isPendingA && !isPendingB) return -1
        if (!isPendingA && isPendingB) return 1
        return 0
      }),
    [queuedResolutionsRef],
  )

  const filterThreadsByType = useCallback(
    (threadsToFilter: ThreadData[], type: SortBy) => {
      const filters = {
        [SortBy.PENDING]: (thread: ThreadData) =>
          !thread.resolved && thread.metadata.status === 'pending',
        [SortBy.APPROVED]: (thread: ThreadData) =>
          thread.resolved && thread.metadata.status === 'approved',
        [SortBy.REJECTED]: (thread: ThreadData) =>
          thread.resolved && thread.metadata.status === 'rejected',
        [SortBy.GENERAL]: (thread: ThreadData) =>
          thread.resolved && thread.metadata.type === 'general',
        [SortBy.ALL_TYPES]: (thread: ThreadData) => {
          // For general comments, only show resolved ones
          if (thread.metadata.type === 'general') {
            return thread.resolved
          }
          // For other types, show all
          return true
        },
      }

      return threadsToFilter.filter(filters[type])
    },
    [queuedResolutionsRef],
  )

  const filteredThreads = useMemo(() => {
    // First apply queued resolutions to create an updated thread list
    const threadsWithQueuedResolutions = threads.map(thread => {
      const queuedResolution = queuedResolutionsRef.current.get(thread.id)
      if (queuedResolution?.status) {
        return {
          ...thread,
          resolved: true,
          metadata: {
            ...thread.metadata,
            status: queuedResolution.status,
          },
        }
      }
      return thread
    })

    // Then filter and sort the updated threads
    const filtered = filterThreadsByType(threadsWithQueuedResolutions, selectedCommentType)
    return selectedCommentType === SortBy.ALL_TYPES ? sortThreads(filtered) : filtered
  }, [threads, selectedCommentType, filterThreadsByType, sortThreads, queuedResolutionsRef])

  // Check if there are unresolved general comments
  const hasUnresolvedGeneralComments = useMemo(
    () => threads.some(thread => !thread.resolved && thread.metadata.type === 'general'),
    [threads],
  )

  const shouldPersist = useCallback(async () => {
    const queuedResolutions = Array.from(queuedResolutionsRef.current.values())
    const hasQueuedResolutions = queuedResolutions.length > 0
    const allRejected =
      hasQueuedResolutions && queuedResolutions.every(({ status }) => status === 'rejected')

    // Only update liveblocks if there are queued resolutions and they are all rejected (do not update backend for no purpose but reflect changes in liveblocks history)
    if (hasQueuedResolutions && allRejected) {
      await handlePendingThreadResolutions()
      return false
    }

    return true
  }, [handlePendingThreadResolutions, queuedResolutionsRef])

  const afterSave = useCallback(
    async (isPoweredTemplate: boolean) => {
      // Resolve any pending threads after successful save
      await handlePendingThreadResolutions()

      if (!isPoweredTemplate) {
        broadcast({ type: 'REFETCH' })
        reviewLock.releaseLock()
      }
    },
    [handlePendingThreadResolutions, broadcast, reviewLock.releaseLock],
  )

  return (
    <EditList
      threads={threads}
      activeCommentTarget={activeCommentTarget}
      setActiveCommentTarget={setActiveCommentTarget}
      hasPendingComments={hasPendingComments}
      checkPendingStatus={checkPendingStatus}
      updateLocalThreadStatus={updateLocalThreadStatus}
      queueThreadResolution={queueThreadResolution}
      handlePendingThreadResolutions={handlePendingThreadResolutions}
      queuedResolutionsRef={queuedResolutionsRef}
      createThread={handleCreateThread}
      editThreadMetadata={editThreadMetadata}
      selectedCommentType={selectedCommentType}
      setSelectedCommentType={setSelectedCommentType}
      threadsToDisplayInSidePanel={filteredThreads}
      hasUnresolvedGeneralComments={hasUnresolvedGeneralComments}
      handleRefetch={handleRefetch}
      reviewModeHandlers={{
        isOwner: reviewLock.isOwner ?? false,
        setLock: reviewLock.setLock,
        releaseLock: reviewLock.releaseLock,
        isLocked: reviewLock.isLocked ?? false,
      }}
      shouldPersist={shouldPersist}
      afterSave={afterSave}
    />
  )
}

export const LiveblocksEditList = () => (
  <LiveblocksErrorBoundary>
    <LiveblocksEditListContent />
  </LiveblocksErrorBoundary>
)
